// Copyright 2026. WebPros International GmbH. All rights reserved.

import * as React from 'react';
import {
    Alert,
    CodeEditor,
    Form,
    FormField,
    FormFieldText,
    setIn,
    Text,
    Translate,
} from '@plesk/ui-library';
import { PageHeader } from 'admin/common/components/PageHeader/PageHeader';
import { Loader } from 'common/components';
import {
    INTENT_TYPE,
    SIZE,
} from 'common/constants';
import { SelectWithDataLoader } from 'admin/common/components/SelectWithDataLoader/SelectWithDataLoader';
import { Button } from 'admin/common/components/Button/Button';
import * as computeResourceActions from 'admin/computeResource/actions';
import * as usersActions from 'admin/user/actions';
import {
    bindActionCreators,
    Dispatch,
} from 'redux';
import { RootState } from 'admin/core/store';
import { LOADING_FLAGS } from 'common/modules/app/loadingFlags/constants';
import { IComputeResourceResponse } from 'common/api/resources/ComputeResource';
import { IUserResponse } from 'common/api/resources/User';
import { IMassMailUsersRequest } from 'common/api/resources/MassMailing';
import * as formErrorsActions from 'common/modules/app/formErrors/actions';
import {
    requiredRule,
    validate,
} from 'common/validator';
import * as massMailingActions from 'admin/massMailing/actions';
import { MASS_MAILING_FORM } from 'admin/massMailing/constants';
import { HTTP_CODES } from 'common/api/constants';
import { getProcessedErrors } from 'common/modules/app/formErrors/selectors';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { pathTo } from 'common/helpers/core';
import { hasPermission } from 'common/modules/permission/selectors';
import { PERMISSION_LIST } from 'common/modules/permission/constants';
import { isMailDisabledInSettings } from 'common/modules/settings/selectors';
import {
    IVariable,
    SystemVariablesPopover,
} from 'admin/common/components/SystemVariablesPopover/SystemVariablesPopover';
import { ExampleTemplatePopover } from 'admin/massMailing/components/ExampleTemplatePopover';
import PreviewDialog from 'admin/massMailing/components/PreviewDialog';
import {
    ErrorMessage,
    FooterButtonGroup,
    MassMailingFormFooter,
    NoWrapCode,
    ResizableCodeEditor,
    SelectWithDescription,
} from 'admin/massMailing/containers/Styles';
import { track } from 'common/analytics/mixpanel';
import { ANALYTICS_EVENTS } from 'common/analytics/constants';

const crLoop = <NoWrapCode>{'{% for cr in crs %}'}</NoWrapCode>;
const vmLoop = <NoWrapCode>{'{% for vm in cr.vms %}'}</NoWrapCode>;
const crLocationsExample = <NoWrapCode>{'{{ cr.locations|join(\', \') }}'}</NoWrapCode>;

const defaultValues: IMassMailUsersRequest = {
    related_compute_resource_ids: [],
    user_ids: [],
    message: '',
    subject: '',
};

// The Subject field is intentionally made required only in the form (it's still nullable in the API) to
// avoid breaking changes to the API
const getRequiredFieldsRules = () => ({
    subject: requiredRule(<Translate content="validate.fieldRequired" />),
    message: requiredRule(<Translate content="validate.fieldRequired" />),
});

export type MassMailingProps =
    ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps>;

export const MassMailingForm: React.FC<MassMailingProps> = ({
    isSubmittingForm,
    formErrors,
    isMailDisabled,
    canViewMailSettings,
    formErrorsActions: {
        setFormErrors,
        clearFormErrors,
    },
    massMailingActions: {
        sendEmailToSelectedUsers,
    },
    computeResourceActions: {
        getComputeResources,
    },
    usersActions: {
        getUsers,
    },
}) => {

    React.useEffect(() => {
        track(ANALYTICS_EVENTS.ADMIN.MASS_MAILING.VISIT);
    }, []);

    const [submitValues, setSubmitValues] = React.useState(defaultValues);
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [errorMessage, setErrorMessage] = React.useState('');
    const [isPreviewDialogOpen, setPreviewDialogOpen] = React.useState(false);

    const templateVariables: IVariable[] = [
        {
            variable: 'email',
            description: <Translate content="massMailing.form.variables.email" />,
        },
        {
            variable: 'vm_count',
            description: <Translate content="massMailing.form.variables.vmCount" />,
        },
        {
            variable: 'cr.name',
            description: <Translate content="massMailing.form.variables.crName" params={{ crLoop }} />,
        },
        {
            variable: 'cr.locations',
            description: (
                <Translate
                    content="massMailing.form.variables.crLocations"
                    params={{
                        crLoop,
                        example: crLocationsExample,
                    }}
                />
            ),
        },
        {
            variable: 'vm.name',
            description: <Translate content="massMailing.form.variables.vmName" params={{ vmLoop }} />,
        },
        {
            variable: 'vm.description',
            description: <Translate content="massMailing.form.variables.vmDescription" params={{ vmLoop }} />,
        },
        {
            variable: 'vm.ipv4',
            description: <Translate content="massMailing.form.variables.vmIpv4" params={{ vmLoop }} />,
        },
        {
            variable: 'vm.ipv6',
            description: <Translate content="massMailing.form.variables.vmIpv6" params={{ vmLoop }} />,
        },
        {
            variable: 'vm.status',
            description: <Translate content="massMailing.form.variables.vmStatus" params={{ vmLoop }} />,
        },
        {
            variable: 'cr.id',
            description: <Translate content="massMailing.form.variables.crId" params={{ crLoop }} />,
        },
        {
            variable: 'vm.id',
            description: <Translate content="massMailing.form.variables.vmId" params={{ vmLoop }} />,
        },
        {
            variable: 'vm.uuid',
            description: <Translate content="massMailing.form.variables.vmUuid" params={{ vmLoop }} />,
        },
    ];

    React.useEffect(() => () => {
        clearFormErrors();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSubmit = async (values: IMassMailUsersRequest) => {

        if (isSubmitting || isMailDisabled) {
            return;
        }

        setIsSubmitting(true);

        clearFormErrors();
        setErrorMessage('');

        try {
            const rules = getRequiredFieldsRules();

            if (values.related_compute_resource_ids.length === 0) {
                rules['user_ids'] = requiredRule(<Translate content="massMailing.form.recipientsRequired" />);
            }

            if (values.user_ids.length === 0) {
                rules['related_compute_resource_ids'] = requiredRule(<Translate content="massMailing.form.recipientsRequired" />);
            }

            const errors = validate<IMassMailUsersRequest>(values, rules);

            if (Object.keys(errors).length) {
                setFormErrors(errors);
                return;
            }

            await sendEmailToSelectedUsers(values);
        } catch (e) {
            if (e.response.status === HTTP_CODES.VALIDATION_ERROR) {
                // Field-level errors are shown inline by the Form via `formErrors`,
                // already populated by the NetworkService interceptor on this response.
                return;
            }

            if (e.response.status >= HTTP_CODES.BAD_REQUEST) {
                setErrorMessage(e.response.data.message);
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleFieldChange = (field: string, value: string) => setSubmitValues(setIn(submitValues, field, value));

    const handleOpenPreview = () => {
        clearFormErrors();

        const errors = validate<IMassMailUsersRequest>(submitValues, getRequiredFieldsRules());

        if (Object.keys(errors).length) {
            setFormErrors(errors);
            return;
        }

        setPreviewDialogOpen(true);
    };

    const handleClosePreview = () => setPreviewDialogOpen(false);

    return (
        <>
            <PageHeader
                isButtonShown={false}
                title={<Translate content="massMailing.form.header"/>}
            />
            <Loader isLoading={false}>
                <Form
                    id="massMailUsersForm"
                    errors={formErrors}
                    values={submitValues}
                    onSubmit={handleSubmit}
                    hideRequiredLegend={true}
                    applyButton={false}
                    cancelButton={false}
                    submitButton={false}
                    vertical={true}
                    onFieldChange={handleFieldChange}
                    style={{ maxWidth: '420px' }}
                >
                    {isMailDisabled && (
                        <Alert intent={INTENT_TYPE.WARNING} data-cy={MASS_MAILING_FORM.MAIL_DISABLED_ALERT}>
                            <Translate content="massMailing.form.mailDisabledAlert.description" />
                            {canViewMailSettings && (
                                <>
                                    {' '}
                                    <Link to={pathTo('settings/mail')}>
                                        <Translate content="massMailing.form.mailDisabledAlert.link" />
                                    </Link>
                                </>
                            )}
                        </Alert>
                    )}
                    <SelectWithDescription>
                        <SelectWithDataLoader
                            name="related_compute_resource_ids"
                            label="massMailing.form.relatedComputeResources"
                            loadItems={getComputeResources}
                            buttonLabel="massMailing.form.selectAllComputeResources"
                            mapper={(cr: IComputeResourceResponse) => ({
                                label: cr.name,
                                value: cr.id,
                            })}
                            onChange={(related_compute_resource_ids: number[]) => setSubmitValues({
                                ...submitValues,
                                related_compute_resource_ids,
                            })}
                            values={[]}
                        />
                        <Text
                            fontSize={SIZE.SM}
                            intent={INTENT_TYPE.MUTED}
                        >
                            <Translate content="massMailing.form.relatedComputeResourcesDescription" />
                        </Text>
                    </SelectWithDescription>
                    <SelectWithDataLoader
                        name="user_ids"
                        label="massMailing.form.users"
                        loadItems={getUsers}
                        mapper={(user: IUserResponse) => ({
                            label: user.email,
                            value: user.id,
                        })}
                        onChange={(selectedUsers: number[]) => setSubmitValues({
                            ...submitValues,
                            user_ids: selectedUsers,
                        })}
                        values={[]}
                    />
                    <FormFieldText
                        name="subject"
                        label={<Translate content="massMailing.form.subject" />}
                        size={SIZE.FILL}
                        required={true}
                    />
                    <FormField
                        name="message"
                        data-cy={MASS_MAILING_FORM.MESSAGE_FIELD}
                        label={<Translate content="massMailing.form.message" />}
                        required={true}
                        description={(
                            <>
                                <Translate
                                    content="massMailing.form.messageTemplateDescription"
                                    params={{ twig: <a rel="noreferrer" href="https://twig.symfony.com/doc/3.x/" target="_blank">Twig</a> }}
                                />
                                <br />
                                <Translate
                                    content="massMailing.form.messageTemplateExampleDescription"
                                    params={{
                                        example: (
                                            <ExampleTemplatePopover
                                                currentMessage={submitValues.message}
                                                onAdd={(message) => handleFieldChange('message', message)}
                                            />
                                        ),
                                    }}
                                />
                            </>
                        )}
                    >
                        <ResizableCodeEditor height={280}>
                            <CodeEditor
                                id="message"
                                mode={{ name: 'twig', base: 'text/html' }}
                                onChange={(message) => handleFieldChange('message', message)}
                            >
                                {submitValues.message}
                            </CodeEditor>
                        </ResizableCodeEditor>
                    </FormField>
                    <MassMailingFormFooter>
                        <FooterButtonGroup>
                            <Button
                                type="submit"
                                form="massMailUsersForm"
                                fill={false}
                                intent={INTENT_TYPE.PRIMARY}
                                size={SIZE.LG}
                                isLoading={isSubmittingForm}
                                disabled={isMailDisabled}
                            >
                                <Translate content="massMailing.form.submitBtn" />
                            </Button>
                            <Button
                                fill={false}
                                size={SIZE.LG}
                                onClick={handleOpenPreview}
                            >
                                <Translate content="massMailing.form.previewBtn" />
                            </Button>
                        </FooterButtonGroup>
                        <SystemVariablesPopover
                            variables={templateVariables}
                            button={<Translate content="massMailing.form.variablesButton" />}
                            header={<Translate content="massMailing.form.variablesTitle" />}
                            canCloseOnOutsideClick={false}
                        />
                    </MassMailingFormFooter>
                    <Text
                        fontSize={SIZE.SM}
                        intent={INTENT_TYPE.MUTED}
                    >
                        <Translate content="massMailing.form.info" />
                    </Text>
                    <Text>
                        <ErrorMessage>{errorMessage}</ErrorMessage>
                    </Text>
                </Form>
            </Loader>
            <PreviewDialog
                isOpen={isPreviewDialogOpen}
                values={submitValues}
                onClose={handleClosePreview}
            />
        </>
    );
};

const mapStateToProps = (state: RootState) => ({
    isSubmittingForm: state.app.loadingFlags.has(LOADING_FLAGS.MASS_MAIL_SENDING),
    formErrors: getProcessedErrors(state),
    isMailDisabled: isMailDisabledInSettings(state),
    canViewMailSettings: hasPermission(state, PERMISSION_LIST.GET_APP_SETTINGS, PERMISSION_LIST.MANAGE_APP_SETTINGS),
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    computeResourceActions: bindActionCreators(computeResourceActions, dispatch),
    usersActions: bindActionCreators(usersActions, dispatch),
    formErrorsActions: bindActionCreators(formErrorsActions, dispatch),
    massMailingActions: bindActionCreators(massMailingActions, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(MassMailingForm);
